<template>
  <div class="labels-statistics-container ob-scroll">
    <div class="row statistics-filters">
      <div class="col-sm-6 col-md-3 pb-2">
        <div>From</div>
        <board-item-date-picker :mode="'date'" v-model="dateFrom" />
      </div>

      <div class="col-sm-6 col-md-3 pb-2">
        <div>From</div>
        <board-item-date-picker :mode="'date'" v-model="dateTo" />
      </div>
      <div class="col-sm-6 col-md-3 pb-2">
        <div class="cursor-pointer">
          <b-form-checkbox class="mb-1" v-model="useBoardLabelsOnly" :unchecked-value="false">
            Only show board-specific labels
          </b-form-checkbox>
        </div>
      </div>
    </div>

    <div class="actions">
      <div>
        <button class="badgebutton active btn" @click="refreshLabelsStatistic" :disabled="!dateFrom || !dateTo">
          <i class="fa fa-sync"></i>
          Refresh Statistics
        </button>
      </div>
      <div>
        <button class="badgebutton active btn" @click="loadLabelsStatistic" :disabled="!dateFrom || !dateTo">
          <i class="fa fa-download"></i>
          Load Statistics
        </button>
      </div>
    </div>

    <div v-if="!displayEmptyResult" class="container-fluid">
      <div class="row">
        <div class="col-sm-12 col-md-6" v-if="pieStatisticValues && pieStatisticValues.length">
          <div class="pie-statistic">
            <div class="pie-statistic-header">Top {{ pieStatisticValues.length }} used labels</div>
            <apexchart
              :type="pieStatisticOptions.chart.type"
              :width="pieStatisticOptions.chart.width"
              :options="pieStatisticOptions"
              :series="pieStatisticValues"
            ></apexchart>
          </div>

          <hr class="mt-3" />
          <div class="labels-usage">
            <div class="labels-usage-header labels-usage-row">
              <div>Label</div>
              <div>Used in Time Range</div>
            </div>
            <div class="labels-usage-row" v-for="(item, index) in allLabelsUsageStatistics" :key="index">
              <div>{{ item.labelTitle }}</div>
              <div>{{ item.usedCount }}</div>
            </div>
          </div>
          <hr />
        </div>

        <div class="col-sm-12 col-md-6" v-if="barStatisticValues && barStatisticValues.length">
          <apexchart
            :type="barStatisticOptions.chart.type"
            :height="barStatisticOptions.chart.height"
            :options="barStatisticOptions"
            :series="barStatisticValues"
          ></apexchart>
        </div>
      </div>
    </div>

    <div v-if="displayEmptyResult" class="p-12">
      <div class="label justify-content-center d-flex align-items-center">
        <i class="far fa-search fa-2x mr-2"></i>
        No Board labels usage found, please modify your query
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { reportsClient } from '@/apiclient/opsboardapi';
  import { GetLabelsStatisticQuery, GeneralLabelUsageInfo, LabelsOverTimeUsageApiDto } from '@/apiclient/apiclient_generated';
  import FileService from '@/services/files.service';
  import PublicConstants from '@/types/publicConstants';
  import { DateTime } from 'luxon';

  @Component({
    components: {},
  })
  export default class LabelsStatistics extends Vue {
    dateFrom: any = new Date();
    dateTo: any = new Date();
    useBoardLabelsOnly = false;
    displayEmptyResult = false;

    allLabelsUsageStatistics: GeneralLabelUsageInfo[] = [];

    pieStatisticOptions = { chart: { type: '', width: 0 }, labels: [], colors: [], legend: {} };
    pieStatisticValues = [];

    barStatisticOptions = {
      chart: { type: '', height: 0, stacked: true, toolbar: {}, zoom: {} },
      colors: [],
      xaxis: { type: '', categories: [] },
    };
    barStatisticValues = [];

    private maxTopUsedLabelsCount = 5;

    async refreshLabelsStatistic() {
      const query = this.getStatisticQuery();

      const labelsUsageStatistics: GeneralLabelUsageInfo[] = await reportsClient.getLabelsUsageStatistics(query);
      const labelsOverTimeUsageStatistics: LabelsOverTimeUsageApiDto = await reportsClient.getLabelsOverTimeUsageStatistic(query);

      this.displayEmptyResult = !labelsUsageStatistics?.length || !labelsOverTimeUsageStatistics?.labels?.length;

      if (this.displayEmptyResult) {
        return;
      }

      this.refreshLabelsUsageStatistics(labelsUsageStatistics);
      this.refreshLabelsOverTimeUsageStatistics(labelsOverTimeUsageStatistics);
    }

    private refreshLabelsUsageStatistics(labelsUsageStatistics: GeneralLabelUsageInfo[]) {
      this.initPieStatisticOptions();

      this.allLabelsUsageStatistics = labelsUsageStatistics;
      this.pieStatisticOptions.colors = this.allLabelsUsageStatistics.map((t) => t.labelColor);

      if (labelsUsageStatistics.length >= this.maxTopUsedLabelsCount) {
        const topUsedLabels = labelsUsageStatistics.slice(0, this.maxTopUsedLabelsCount);

        const others = {
          labelTitle: 'Others',
          usedCount: labelsUsageStatistics.slice(this.maxTopUsedLabelsCount).reduce((counter, entity) => {
            return counter + entity.usedCount;
          }, 0),
          labelColor: PublicConstants.othersLabelsColor,
        } as GeneralLabelUsageInfo;

        this.pieStatisticOptions.colors[this.pieStatisticOptions.colors.length - 1] = PublicConstants.othersLabelsColor;

        this.setPieStatisticData([...topUsedLabels, others]);
      } else {
        this.setPieStatisticData(labelsUsageStatistics);
      }
    }

    private refreshLabelsOverTimeUsageStatistics(labelsOverTimeUsageStatistics: LabelsOverTimeUsageApiDto) {
      this.initBarStatisticOptions();

      this.barStatisticOptions.xaxis.categories = labelsOverTimeUsageStatistics.columnDates.map((t) =>
        DateTime.fromISO(t).toFormat('MMM-yyyy')
      );

      this.barStatisticOptions.colors = labelsOverTimeUsageStatistics.labelsColors;
      this.barStatisticValues = labelsOverTimeUsageStatistics.labels;
    }

    private setPieStatisticData(statisticsSource: GeneralLabelUsageInfo[]) {
      this.pieStatisticOptions.labels = statisticsSource.map((t) => t.labelTitle);
      this.pieStatisticValues = statisticsSource.map((t) => t.usedCount);
    }

    async loadLabelsStatistic() {
      const query = this.getStatisticQuery();

      await this.refreshLabelsStatistic();

      const statistic = await reportsClient.getLabelsReport(query);

      FileService.downloadFile(statistic.data, statistic.fileName);
    }

    private getStatisticQuery() {
      return {
        boardId: this.$route.params.id,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        useOnlyBoardSpecificLabels: this.useBoardLabelsOnly,
      } as GetLabelsStatisticQuery;
    }

    initPieStatisticOptions() {
      this.pieStatisticOptions = {
        chart: {
          type: 'pie',
          width: 500,
        },

        labels: [],

        colors: [],

        legend: {
          position: 'bottom',
        },
      };
    }

    initBarStatisticOptions() {
      this.barStatisticOptions = {
        chart: {
          type: 'bar',
          height: 480,
          stacked: true,
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false,
          },
        },

        colors: [],

        xaxis: {
          type: 'category',
          categories: [],
        },
      };
    }
  }
</script>

<style scoped lang="scss">
  .labels-statistics-container {
    height: 70vh;
    padding: 0 4px;
    overflow-y: auto;
    overflow-x: hidden;

    .statistics-filters {
      display: flex;
      justify-content: flex-start;
      align-items: flex-end;
    }

    .actions {
      display: flex;
      justify-content: flex-start;
      gap: 10px;
      align-items: center;

      padding: 20px 0;
    }

    .pie-statistic {
      text-align: -moz-center;
      text-align: -webkit-center;

      &-header {
        font-size: 20px;
        font-weight: 700;
      }
    }

    .labels-usage {
      &-header {
        font-size: 16px;
        font-weight: 700;
      }

      &-row {
        display: grid;
        grid-template-columns: 200px 300px;
        justify-content: flex-start;
        align-items: center;
        gap: 10px;
      }
    }
  }
</style>

<style lang="scss">
  .custom-control-label {
    cursor: pointer !important;
  }
</style>
